import React, { useCallback, useEffect, useState } from 'react';
import { FormikProvider, useFormik } from 'formik';
import { Form, Switch } from 'formik-antd';
import * as Yup from 'yup';
import { useDidUpdate } from 'rooks';

import FormItem from 'components/formik/FormItem';
import BaseAlert from 'components/wrappers/BaseAlert';
import {
  updateEmailDeliveryConfiguration,
  getEmailDeliveryConfiguration,
} from 'redux/reducers/accountSlice';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import {
  BackofficeUserModel,
  UserEmailSettingsModel,
} from 'api/backofficeApiClient';
import { checkIsCompanyOwner } from 'utils/userRolesUtils';
import {
  userSelector,
  isCorporateTASelector,
} from 'redux/selectors/authenticationSelectors';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from '../AccountSettingsPage.module.less';

interface UserSettingsViewModel extends UserEmailSettingsModel {
  isAssignmentEndingSoon: boolean;
}

const getCorrectValues = (
  data: UserEmailSettingsModel,
): UserSettingsViewModel => {
  const isActive =
    data.isAssignmentEndingSoon30 ||
    data.isAssignmentEndingSoon60 ||
    data.isAssignmentEndingSoon90 ||
    false;

  return {
    ...data,
    isAssignmentEndingSoon: isActive,
  };
};

function getValidationSchema(
  user: BackofficeUserModel | null,
  isCorporateTA: boolean | undefined,
) {
  const validationSchema: any = Yup.object({
    isAssignmentApproval: Yup.boolean(),
    isAssignmentAdditionalInfoRequired: Yup.boolean(),
    isAssignmentEndingSoon: Yup.boolean(),
    isAssignmentEndingSoon30: Yup.boolean(),
    isAssignmentEndingSoon60: Yup.boolean(),
    isAssignmentEndingSoon90: Yup.boolean(),
  });

  if (checkIsCompanyOwner(user)) {
    validationSchema.isPartnershipRequest = Yup.boolean();
    validationSchema.isPricingAgreementUpdate = Yup.boolean();
    validationSchema.isMemberOnboardingInformation = Yup.boolean();
  }

  if (!isCorporateTA) {
    validationSchema.isCustomerApproval = Yup.boolean();
    validationSchema.isCustomerAdditionalInfoRequired = Yup.boolean();
  }

  return validationSchema;
}

function getInitialValues(
  user: BackofficeUserModel | null,
  isCorporateTA: boolean | undefined,
) {
  let initialValues: UserSettingsViewModel = {
    isAssignmentApproval: false,
    isAssignmentAdditionalInfoRequired: false,
    isAssignmentEndingSoon: false,
    isAssignmentEndingSoon30: false,
    isAssignmentEndingSoon60: false,
    isAssignmentEndingSoon90: false,
  };

  if (checkIsCompanyOwner(user)) {
    initialValues.isPartnershipRequest = false;
    initialValues.isPricingAgreementUpdate = false;
    initialValues.isMemberOnboardingInformation = false;
  }

  if (!isCorporateTA) {
    initialValues.isCustomerApproval = false;
    initialValues.isCustomerAdditionalInfoRequired = false;
  }
  return initialValues;
}

export default function NotificationSettingsPage() {
  const [whichFieldDisable, setWhichFieldDisable] = useState<string[]>([]);
  const [isInitialLoaded, setIsInitialLoaded] = useState(false);
  const [emailDeliveryConfiguration, setEmailDeliveryConfiguration] =
    useState<UserSettingsViewModel>();
  const user = useAppSelector(userSelector);
  const isCompanyOwner = checkIsCompanyOwner(user);
  const isCorporateTA = useAppSelector(isCorporateTASelector);
  const dispatch = useAppDispatch();
  const formik = useFormik({
    initialValues:
      emailDeliveryConfiguration || getInitialValues(user, isCorporateTA),
    validationSchema: getValidationSchema(user, isCorporateTA),
    enableReinitialize: true,
    onSubmit: (data: UserSettingsViewModel) => {},
  });

  useEffect(() => {
    loadEmailDeliveryConfiguration();
    // eslint-disable-next-line
  }, []);

  useDidUpdate(() => {
    whichFieldDisable.length && submitForm();
  }, [whichFieldDisable]);

  const loadEmailDeliveryConfiguration = useCallback(async () => {
    const result = await dispatch(getEmailDeliveryConfiguration());

    if (getEmailDeliveryConfiguration.fulfilled.match(result)) {
      setEmailDeliveryConfiguration(getCorrectValues(result.payload));
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
    setIsInitialLoaded(true);
  }, [dispatch]);

  const submitForm = async () => {
    const { isAssignmentEndingSoon, ...anotherValues } = formik.values;

    const result = await dispatch(
      updateEmailDeliveryConfiguration(anotherValues),
    );

    if (!updateEmailDeliveryConfiguration.fulfilled.match(result)) {
      loadEmailDeliveryConfiguration();
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
    setWhichFieldDisable([]);
  };

  const handleChangeAssignmentEnding = (checked: boolean) => {
    setWhichFieldDisable([
      'isAssignmentEndingSoon',
      'isAssignmentEndingSoon30',
      'isAssignmentEndingSoon60',
      'isAssignmentEndingSoon90',
    ]);
    formik.setFieldValue('isAssignmentEndingSoon30', checked);
    formik.setFieldValue('isAssignmentEndingSoon60', checked);
    formik.setFieldValue('isAssignmentEndingSoon90', checked);
  };

  const handleChangeField = (field: string) => {
    setWhichFieldDisable([field]);
  };

  const isLoading = (field: string) => {
    return !isInitialLoaded || whichFieldDisable.includes(field);
  };

  return (
    <div className={styles.pageBox}>
      <FormikProvider value={formik}>
        <Form layout="vertical">
          <div className={styles.sectionBox}>
            <h4 className={styles.title}>
              Assignment related email notifications
            </h4>
            <FormItem
              name="isAssignmentApproval"
              className={styles.formItemBox}
            >
              <div className={styles.switchBox}>
                <Switch
                  checkedChildren="On"
                  unCheckedChildren="Off"
                  name="isAssignmentApproval"
                  loading={isLoading('isAssignmentApproval')}
                  onChange={() => handleChangeField('isAssignmentApproval')}
                />
              </div>
              <div className={styles.textBox}>
                <span className={styles.subtitle}>Assignment Approval</span>
                <div className={styles.text}>
                  Email notifications upon approval of the submitted assignment
                  by the Admin.
                </div>
              </div>
            </FormItem>
            <FormItem
              name="isAssignmentAdditionalInfoRequired"
              className={styles.formItemBox}
            >
              <div className={styles.switchBox}>
                <Switch
                  checkedChildren="On"
                  unCheckedChildren="Off"
                  name="isAssignmentAdditionalInfoRequired"
                  loading={isLoading('isAssignmentAdditionalInfoRequired')}
                  onChange={() =>
                    handleChangeField('isAssignmentAdditionalInfoRequired')
                  }
                />
              </div>
              <div className={styles.textBox}>
                <span className={styles.subtitle}>
                  Missing Assignment Information
                </span>
                <div className={styles.text}>
                  Email notifications when information is needed for your
                  assignment(s). Turn this notification Off to stop receiving
                  emails from Admin when information is missing for your
                  assignments. You will still receive In-App notifications.
                </div>
              </div>
            </FormItem>
            {isCompanyOwner && (
              <FormItem
                name="isPartnershipRequest"
                className={styles.formItemBox}
              >
                <div className={styles.switchBox}>
                  <Switch
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    name="isPartnershipRequest"
                    loading={isLoading('isPartnershipRequest')}
                    onChange={() => handleChangeField('isPartnershipRequest')}
                  />
                </div>
                <div className={styles.textBox}>
                  <span className={styles.subtitle}>
                    Assignment Partner Split
                  </span>
                  <div className={styles.text}>
                    Email notification from Partner members regarding any split
                    commissions for entered assignments.
                  </div>
                </div>
              </FormItem>
            )}

            <div className={styles.formItemBox}>
              <div className={styles.switchBox}>
                <Switch
                  name="isAssignmentEndingSoon"
                  checked={
                    formik.values.isAssignmentEndingSoon30 ||
                    formik.values.isAssignmentEndingSoon60 ||
                    formik.values.isAssignmentEndingSoon90
                  }
                  checkedChildren="On"
                  unCheckedChildren="Off"
                  loading={isLoading('isAssignmentEndingSoon')}
                  onChange={handleChangeAssignmentEnding}
                />
              </div>
              <div className={styles.textBox}>
                <span className={styles.subtitle}>Assignment Ending Soon</span>
                <div className={styles.text}>
                  Email notifications when an active assignment is ending in 30,
                  60, 90 days. Turn this notification Off to stop receiving All
                  email notifications for assignments ending soon.
                </div>
              </div>
            </div>
            <div className={styles.nestBox}>
              <FormItem
                name="isAssignmentEndingSoon90"
                className={styles.formItemBox}
              >
                <div className={styles.switchBox}>
                  <Switch
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    name="isAssignmentEndingSoon90"
                    loading={isLoading('isAssignmentEndingSoon90')}
                    onChange={() =>
                      handleChangeField('isAssignmentEndingSoon90')
                    }
                  />
                </div>
                <div className={styles.textBox}>
                  <span className={styles.subtitle}>
                    Assignment Ending in 90 days
                  </span>
                  <div className={styles.text}>
                    Email notification when an active assignment is ending in 90
                    days.
                  </div>
                </div>
              </FormItem>
              <FormItem
                name="isAssignmentEndingSoon60"
                className={styles.formItemBox}
              >
                <div className={styles.switchBox}>
                  <Switch
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    name="isAssignmentEndingSoon60"
                    loading={isLoading('isAssignmentEndingSoon60')}
                    onChange={() =>
                      handleChangeField('isAssignmentEndingSoon60')
                    }
                  />
                </div>
                <div className={styles.textBox}>
                  <span className={styles.subtitle}>
                    Assignment Ending in 60 days
                  </span>
                  <div className={styles.text}>
                    Email notification when an active assignment is ending in 60
                    days.
                  </div>
                </div>
              </FormItem>
              <FormItem
                name="isAssignmentEndingSoon30"
                className={styles.formItemBox}
              >
                <div className={styles.switchBox}>
                  <Switch
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    name="isAssignmentEndingSoon30"
                    loading={isLoading('isAssignmentEndingSoon30')}
                    onChange={() =>
                      handleChangeField('isAssignmentEndingSoon30')
                    }
                  />
                </div>
                <div className={styles.textBox}>
                  <span className={styles.subtitle}>
                    Assignment Ending in 30 days
                  </span>
                  <div className={styles.text}>
                    Email notification when an active assignment is ending in 30
                    days.
                  </div>
                </div>
              </FormItem>
            </div>
          </div>
          {isCompanyOwner && (
            <div className={styles.sectionBox}>
              <h4 className={styles.title}>
                Profile related email notifications:
              </h4>
              <FormItem
                name="isPricingAgreementUpdate"
                className={styles.formItemBox}
              >
                <div className={styles.switchBox}>
                  <Switch
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    name="isPricingAgreementUpdate"
                    loading={isLoading('isPricingAgreementUpdate')}
                    onChange={() =>
                      handleChangeField('isPricingAgreementUpdate')
                    }
                  />
                </div>
                <div className={styles.textBox}>
                  <span className={styles.subtitle}>
                    Pricing Agreement Updates
                  </span>
                  <div className={styles.text}>
                    Email notification when an update is made by the Admin the
                    pricing agreement.
                  </div>
                </div>
              </FormItem>
              <FormItem
                name="isMemberOnboardingInformation"
                className={styles.formItemBox}
              >
                <div className={styles.switchBox}>
                  <Switch
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    name="isMemberOnboardingInformation"
                    loading={isLoading('isMemberOnboardingInformation')}
                    onChange={() =>
                      handleChangeField('isMemberOnboardingInformation')
                    }
                  />
                </div>
                <div className={styles.textBox}>
                  <span className={styles.subtitle}>
                    Member Onboarding Information
                  </span>
                  <div className={styles.text}>
                    Email notification from the Admin when information is
                    missing for your onboarding. You will still receive In-App
                    notifications for these requests.
                  </div>
                </div>
              </FormItem>
            </div>
          )}
          {!isCorporateTA && (
            <div className={styles.sectionBox}>
              <h4 className={styles.title}>Customer related notifications:</h4>
              <FormItem
                name="isCustomerApproval"
                className={styles.formItemBox}
              >
                <div className={styles.switchBox}>
                  <Switch
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    name="isCustomerApproval"
                    loading={isLoading('isCustomerApproval')}
                    onChange={() => handleChangeField('isCustomerApproval')}
                  />
                </div>
                <div className={styles.textBox}>
                  <span className={styles.subtitle}>
                    Customer Onboarding Approval
                  </span>
                  <div className={styles.text}>
                    Email notification when Admin approves the onboarding of
                    your customer and all of the information has been processed
                    successfully.
                  </div>
                </div>
              </FormItem>
              <FormItem
                name="isCustomerAdditionalInfoRequired"
                className={styles.formItemBox}
              >
                <div className={styles.switchBox}>
                  <Switch
                    checkedChildren="On"
                    unCheckedChildren="Off"
                    name="isCustomerAdditionalInfoRequired"
                    loading={isLoading('isCustomerAdditionalInfoRequired')}
                    onChange={() =>
                      handleChangeField('isCustomerAdditionalInfoRequired')
                    }
                  />
                </div>
                <div className={styles.textBox}>
                  <span className={styles.subtitle}>
                    Missing Customer Information
                  </span>
                  <div className={styles.text}>
                    Email notifications when information is needed for your
                    customer(s). Turn this notification Off to stop receiving
                    emails from Admin when information is missing for your
                    Customer(s). You will still receive In-App notifications.
                  </div>
                </div>
              </FormItem>
            </div>
          )}
        </Form>
      </FormikProvider>
    </div>
  );
}
